import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { Box, IconButton, Stack, Tab, Tabs, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Payout } from '@api/types/commissions';
import { GenericTable } from '@components';
import {
  PAYOUT_STATUS_MAP,
  PayoutStatus as PayoutStatusEnum,
  TOAST_TYPES,
} from '@constants/constants';
import PayoutStatus from './PayoutStatus';
import Pagination from '../../Pagination';
import { formatCurrency } from '@utils/common';
import { setToastMessage } from '@stores/App/slice';
import { useLazyDownloadPayoutsQuery } from '@api/commissions';
import { RootState } from '@stores/store';

interface PayoutsProps {
  data: Payout[];
  isLoading: boolean;
  fetchPayouts: (inputProps: {
    pageNumber?: number;
    pageSize?: number;
    status?: PayoutStatusEnum[];
  }) => void;
  page: number;
  size: number;
  total: number;
  selectedPayoutStatus: PayoutStatusEnum | 'All';
  setSelectedPayoutStatus: (status: PayoutStatusEnum | 'All') => void;
  filteredReferrer: string;
  filteredPayoutStatus: PayoutStatusEnum[];
}

const Payouts: React.FC<PayoutsProps> = ({
  data,
  isLoading,
  fetchPayouts,
  page,
  size,
  total,
  selectedPayoutStatus,
  setSelectedPayoutStatus,
  filteredReferrer,
  filteredPayoutStatus,
}) => {
  const dispatch = useDispatch();
  const userRole = useSelector((state: RootState) => state.UserData.user.role);

  const [showPayoutStatus, setShowPayoutStatus] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<Payout | null>(null);

  const [downloadPayouts] = useLazyDownloadPayoutsQuery();

  const isAdmin = userRole === 'ADMIN';

  const tableData = data.map((payout) => ({
    ...payout,
    id: payout.paymentId,
  }));

  const handleActionClick = (rowId: string) => {
    const row = tableData.find((payout) => payout.id === rowId);
    setSelectedRowData(row || null);
    setShowPayoutStatus(true);
  };

  const handlePayoutStatusChange = () => {
    setShowPayoutStatus(false);
    setSelectedRowData(null);
    fetchPayouts({});
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: PayoutStatusEnum | 'All') => {
    setSelectedPayoutStatus(newValue);

    switch (newValue) {
      case PayoutStatusEnum.PENDING:
        fetchPayouts({
          pageNumber: 0,
          status: [PayoutStatusEnum.PENDING, PayoutStatusEnum.INITIATED],
        });
        break;
      case PayoutStatusEnum.PAID:
        fetchPayouts({
          pageNumber: 0,
          status: [PayoutStatusEnum.PAID],
        });
        break;
      case 'All':
        fetchPayouts({
          pageNumber: 0,
          status: [],
        });
        break;
    }
  };

  const handleDownload = async () => {
    try {
      const params = {
        referrerId: filteredReferrer === 'All' ? undefined : filteredReferrer,
        status: filteredPayoutStatus,
      };

      params.status = params.status && params.status.length > 0 ? params.status : [];

      const { data } = await downloadPayouts(params).unwrap();

      if (data?.url) {
        const link = document.createElement('a');
        link.href = data.url;
        link.download = 'payouts.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: 'Unable to download CSV, please try again later.',
        }),
      );
    }
  };

  const actions = [
    {
      action: handleActionClick,
      condition: (rowId: string) => {
        const row = tableData.find((p) => p.id === rowId);
        return row?.paymentStatus === PayoutStatusEnum.PENDING && isAdmin;
      },
      component: (
        <Typography
          sx={{
            fontSize: '0.875rem',
            fontWeight: 600,
            lineHeight: '1rem',
            color: '#FF8C00',
            cursor: 'pointer',
          }}
        >
          Initiate Payment
        </Typography>
      ),
    },
    {
      action: handleActionClick,
      condition: (rowId: string) => {
        const row = tableData.find((p) => p.id === rowId);
        return row?.paymentStatus === PayoutStatusEnum.PAID && isAdmin;
      },
      component: (
        <Typography
          sx={{
            fontSize: '0.875rem',
            fontWeight: 600,
            lineHeight: '1rem',
            color: '#51B8B0',
            cursor: 'pointer',
          }}
        >
          Details
        </Typography>
      ),
    },
    {
      action: handleActionClick,
      condition: (rowId: string) => {
        const row = tableData.find((p) => p.id === rowId);
        return row?.paymentStatus === PayoutStatusEnum.INITIATED && isAdmin;
      },
      component: (
        <Typography
          sx={{
            fontSize: '0.875rem',
            fontWeight: 600,
            lineHeight: '1rem',
            color: '#51B8B0',
            cursor: 'pointer',
          }}
        >
          Record Payment
        </Typography>
      ),
    },
    {
      action: handleActionClick,
      condition: () => {
        return !isAdmin;
      },
      component: (
        <Typography
          sx={{
            fontSize: '0.875rem',
            fontWeight: 600,
            lineHeight: '1rem',
            color: '#51B8B0',
            cursor: 'pointer',
          }}
        >
          Details
        </Typography>
      ),
    },
  ];

  const columns = [
    {
      key: 'payoutMonth' as keyof Payout,
      label: 'Payout Month',
      render: (value: string | number, row: Payout) => (
        <Typography fontSize="0.875rem" fontWeight={550}>
          {row.payoutMonth}
        </Typography>
      ),
    },
    {
      key: 'payoutDate' as keyof Payout,
      label: 'Payout Date',
      render: (value: string | number, row: Payout) => (
        <Typography fontSize="0.875rem" fontWeight={550}>
          {row.payoutDate ? dayjs(row.payoutDate).format('YYYY-MM-DD hh:mm A') : '-'}
        </Typography>
      ),
    },
    {
      key: 'referrer' as keyof Payout,
      label: 'Referrer',
      render: (value: string | number, row: Payout) => (
        <Typography fontSize="0.875rem" fontWeight={550}>
          {row.referrerName}
        </Typography>
      ),
    },
    {
      key: 'payoutStatus' as keyof Payout,
      label: 'Payout Status',
      render: (value: string | number, row: Payout) => (
        <Typography fontSize="0.875rem" fontWeight={550}>
          {PAYOUT_STATUS_MAP[row.paymentStatus]}
        </Typography>
      ),
    },
    {
      key: 'commission' as keyof Payout,
      label: 'Commission',
      render: (value: string | number, row: Payout) => (
        <Typography fontSize="0.875rem" fontWeight={550}>
          {formatCurrency(row.commission)}
        </Typography>
      ),
    },
    {
      key: 'transactionId' as keyof Payout,
      label: 'Transaction ID',
      render: (value: string | number, row: Payout) => (
        <Typography fontSize="0.875rem" fontWeight={550}>
          {row.transactionId || '-'}
        </Typography>
      ),
    },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        height: 'fit-content',
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '0.75rem',
      }}
    >
      <Stack direction="row" gap="1rem" alignItems="center" justifyContent="space-between">
        <Typography
          sx={{
            fontSize: '1rem',
            lineHeight: '1.375rem',
            fontWeight: 700,
            color: '#162936',
          }}
        >
          Payouts
        </Typography>

        <Stack direction="row" gap="1rem" alignItems="center">
          <IconButton
            disableFocusRipple
            disableRipple
            disableTouchRipple
            onClick={handleDownload}
            sx={{
              color: '#162C36',
              padding: '4px',
            }}
          >
            <FileDownloadOutlinedIcon />
          </IconButton>

          <Pagination
            page={page + 1}
            size={size}
            total={total}
            handleChange={(page) =>
              fetchPayouts({
                pageNumber: page - 1,
              })
            }
            fileName={''}
            chartId={''}
            submoduleId={''}
            filterSelection={{}}
          />
        </Stack>
      </Stack>

      <Tabs
        sx={{
          flexDirection: 'column',
          alignItems: 'flex-start',
          '.MuiTabs-indicator': {
            backgroundColor: '#51B8B0',
            height: '3px',
            borderRadius: '5px',
            overflowX: 'auto',
          },
          '.MuiTabs-scroller': {
            overflowX: 'auto !important',
            scrollbarWidth: 'thin',
          },
        }}
        value={selectedPayoutStatus}
        onChange={handleTabChange}
      >
        <Tab
          sx={{
            fontSize: '0.9rem',
            fontWeight: 700,
            textTransform: 'none',
            color: '#6C6C6C',
            '&.Mui-selected': {
              color: '#51B8B0',
            },
            alignItems: 'center',
          }}
          label="Pending"
          value={PayoutStatusEnum.PENDING}
        />
        <Tab
          sx={{
            fontSize: '0.9rem',
            fontWeight: 700,
            textTransform: 'none',
            color: '#6C6C6C',
            '&.Mui-selected': {
              color: '#51B8B0',
            },
            alignItems: 'center',
          }}
          label="Paid"
          value={PayoutStatusEnum.PAID}
        />
        <Tab
          sx={{
            fontSize: '0.9rem',
            fontWeight: 700,
            textTransform: 'none',
            color: '#6C6C6C',
            '&.Mui-selected': {
              color: '#51B8B0',
            },
            alignItems: 'center',
          }}
          label="All"
          value="All"
        />
      </Tabs>

      <Box
        sx={{
          marginTop: '1rem',
        }}
      >
        <GenericTable<any>
          data={tableData}
          columns={columns}
          meta={{}}
          actions={actions}
          isLoading={isLoading}
        />
      </Box>

      <PayoutStatus
        showPayoutStatus={showPayoutStatus}
        setShowPayoutStatus={setShowPayoutStatus}
        selectedRowData={selectedRowData}
        handlePayoutStatusChange={handlePayoutStatusChange}
      />
    </Box>
  );
};

export default Payouts;
